<template>
  <v-dialog v-model="payment_dialog" :retain-focus="false" :fullscreen="isMobile" persistent>
    <v-card :loading="isLoading">
      <v-card-title text :color="$vuetify.theme.dark ? '' : 'white'" :class="isMobile ? 'pt-12' : ''">
        <span class="headline">{{ $t("bookings.payment") }}</span>
        <v-icon class="ml-2 green--text" @click="savePayment(booking)">
          mdi-content-save-all-outline
        </v-icon>
        <v-spacer></v-spacer>
        <v-icon :class="$vuetify.theme.dark ? 'ml-2 white--text' : 'ml-2 black--text'" @click="closePaymentDialog()">
          mdi-close
        </v-icon>
      </v-card-title>

      <v-card-text>
        <v-row>
          <v-col cols="12" md="4"
            :style="`border-right: 1px solid ${$vuetify.theme.dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'};`">
            <v-row>
              <v-col cols="12">
                <v-toolbar dense elevation="1">
                  <v-toolbar-title @click="copyToBuffer(booking.id)" style="cursor: pointer;">
                    {{ $t("bookings.booking") }} {{ $t("custom.number") }} {{ booking.id }}
                  </v-toolbar-title>
                </v-toolbar>
                <v-simple-table>
                  <template v-slot:default>
                    <tbody>
                      <tr v-if="!booking.hourly && !booking.weekly && !booking.monthly">
                        <th>{{ $t("bookings.days") }}</th>
                        <td @click="copyToBuffer(booking.days)" class="text-right" style="cursor: pointer;">{{
                          booking.days }}</td>
                      </tr>
                      <tr v-if="booking.weekly">
                        <th>{{ $t("custom.weeks") }}</th>
                        <td @click="copyToBuffer(booking.weekly_weeks)" class="text-right" style="cursor: pointer;">{{
                          booking.weekly_weeks }}</td>
                      </tr>
                      <tr v-if="booking.monthly">
                        <th>{{ $t("custom.months") }}</th>
                        <td @click="copyToBuffer(booking.monthly_months)" class="text-right" style="cursor: pointer;">{{
                          booking.monthly_months }}</td>
                      </tr>
                      <tr>
                        <th>{{ $t("bookings.current_price") }}</th>
                        <td @click="copyToBuffer(booking.selected_price)" class="text-right" style="cursor: pointer;">{{
                          booking.selected_price }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</td>
                      </tr>
                      <tr v-if="booking.sale > 0">
                        <th>{{ $t("bookings.sale_percent") }}</th>
                        <td @click="copyToBuffer(booking.sale)" class="text-right" style="cursor: pointer;">{{
                          booking.sale }}%</td>
                      </tr>
                      <tr v-if="booking.sale_cash > 0">
                        <th>{{ $t("bookings.edit_sale_rub", { msg: currency }) }}</th>
                        <td @click="copyToBuffer(booking.sale_cash)" class="text-right" style="cursor: pointer;">{{
                          booking.sale_cash }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</td>
                      </tr>
                      <tr v-if="booking.additional_hours > 0">
                        <th>{{ $t("bookings.add_hours_short") }}</th>
                        <td @click="copyToBuffer(booking.additional_hours)" class="text-right" style="cursor: pointer;">
                          {{ booking.additional_hours }}</td>
                      </tr>
                      <tr v-if="booking.aggr_commission == 0">
                        <th>
                          {{ $t("bookings.rent") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.rental_cost, departure_payments.rent.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.rent && departure_payments.rent.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.rental_cost, departure_payments.rent.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.rental_cost, departure_payments.rent.currency_id) }}
                          </span>
                          <span v-else>{{ booking.rental_cost }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-else>
                        <th>
                          {{ $t("bookings.rent") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.rental_cost - booking.aggr_commission, departure_payments.rent.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.rent && departure_payments.rent.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.rental_cost - booking.aggr_commission, departure_payments.rent.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.rental_cost - booking.aggr_commission,
                              departure_payments.rent.currency_id) }}
                          </span>
                          <span v-else>{{ booking.rental_cost - booking.aggr_commission }} {{ company &&
                            company.multicurrency_cashboxes ? findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.equipment > 0">
                        <th>
                          {{ $t("bookings.other_equipment") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.equipment, departure_payments.equipment.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.equipment && departure_payments.equipment.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.equipment, departure_payments.equipment.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.equipment, departure_payments.equipment.currency_id) }}
                          </span>
                          <span v-else>{{ booking.equipment }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <!-- FT-102: каталожное оборудование -->
                      <tr v-if="booking.addons_cost > 0">
                        <th>
                          {{ $t("booking_addons.title") }}
                        </th>
                        <td @click="copyToBuffer(booking.addons_cost)" class="text-right" style="cursor: pointer;">
                          <span>{{ booking.addons_cost }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.delivery > 0">
                        <th>
                          {{ $t("bookings.delivery") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.delivery, departure_payments.delivery.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.delivery && departure_payments.delivery.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.delivery, departure_payments.delivery.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.delivery, departure_payments.delivery.currency_id) }}
                          </span>
                          <span v-else>{{ booking.delivery }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.delivery_end > 0">
                        <th>
                          {{ $t("bookings.checkout") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.delivery_end, departure_payments.delivery_end.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.delivery_end && departure_payments.delivery_end.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.delivery_end, departure_payments.delivery_end.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.delivery_end,
                              departure_payments.delivery_end.currency_id) }}
                          </span>
                          <span v-else>{{ booking.delivery_end }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.clean_payment > 0">
                        <th>
                          {{ $t("bookings.clean") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.clean_payment, departure_payments.clean.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.clean && departure_payments.clean.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.clean_payment, departure_payments.clean.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.clean_payment, departure_payments.clean.currency_id) }}
                          </span>
                          <span v-else>{{ booking.clean_payment }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.hours_cost > 0">
                        <th>
                          {{ $t("bookings.add_hours_short") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.hours_cost, departure_payments.hours_cost.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.hours_cost && departure_payments.hours_cost.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.hours_cost, departure_payments.hours_cost.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.hours_cost, departure_payments.hours_cost.currency_id)
                            }}
                          </span>
                          <span v-else>{{ booking.hours_cost }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.hours_cost_end > 0">
                        <th>
                          {{ $t("bookings.add_hours_checkout") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.hours_cost_end, departure_payments.hours_cost_end.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.hours_cost_end && departure_payments.hours_cost_end.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.hours_cost_end, departure_payments.hours_cost_end.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.hours_cost_end,
                              departure_payments.hours_cost_end.currency_id) }}
                          </span>
                          <span v-else>{{ booking.hours_cost_end }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.gas > 0">
                        <th>
                          {{ $t("bookings.gas") }}
                        </th>
                        <td @click="copyToBuffer(sumInNonBaseCurrency(booking.gas, departure_payments.gas.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.gas && departure_payments.gas.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.gas, departure_payments.gas.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.gas, departure_payments.gas.currency_id) }}
                          </span>
                          <span v-else>{{ booking.gas }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.insurance > 0">
                        <th>
                          {{ $t("bookings.insurance") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.insurance, departure_payments.insurance.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.insurance && departure_payments.insurance.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.insurance, departure_payments.insurance.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.insurance, departure_payments.insurance.currency_id) }}
                          </span>
                          <span v-else>{{ booking.insurance }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.damage > 0">
                        <th>
                          {{ $t("add_services.damages") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.damage, departure_payments.damage.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.damage && departure_payments.damage.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.damage, departure_payments.damage.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.damage, departure_payments.damage.currency_id) }}
                          </span>
                          <span v-else>{{ booking.damage }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.add_drivers_cost > 0">
                        <th>
                          {{ $t("bookings.add_drivers_small") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.add_drivers_cost, departure_payments.add_drivers_cost.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.add_drivers_cost && departure_payments.add_drivers_cost.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.add_drivers_cost, departure_payments.add_drivers_cost.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.add_drivers_cost,
                              departure_payments.add_drivers_cost.currency_id) }}
                          </span>
                          <span v-else>{{ booking.add_drivers_cost }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.other > 0">
                        <th>
                          {{ $t("bookings.other") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.other, departure_payments.other.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.other && departure_payments.other.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.other, departure_payments.other.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.other, departure_payments.other.currency_id) }}
                          </span>
                          <span v-else>{{ booking.other }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr v-if="booking.other_end > 0">
                        <th>
                          {{ $t("bookings.other_checkout") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(booking.other_end, departure_payments.other_end.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.other_end && departure_payments.other_end.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(booking.other_end, departure_payments.other_end.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(booking.other_end, departure_payments.other_end.currency_id) }}
                          </span>
                          <span v-else>{{ booking.other_end }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr>
                        <th>
                          {{ $t("bookings.deposit") }}
                        </th>
                        <td
                          @click="copyToBuffer(sumInNonBaseCurrency(currentDeposit(), departure_payments.deposit.currency_id))"
                          class="text-right" style="cursor: pointer;">
                          <span
                            v-if="departure_payments && departure_payments.deposit && departure_payments.deposit.currency_id != base_currency_id"
                            @click="copyToBuffer(sumInNonBaseCurrency(currentDeposit(), departure_payments.deposit.currency_id))"
                            style="cursor: pointer;">
                            {{ messageInNonBaseCurrency(currentDeposit(), departure_payments.deposit.currency_id) }}
                          </span>
                          <span v-else>{{ currentDeposit() }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr>
                        <th class="text-left text-overline">
                          {{ $t("custom.total") }}
                        </th>
                        <td @click="copyToBuffer(totalSum())" class="font-weight-bold text-right"
                          style="cursor: pointer;">
                          <span>{{ totalSum() }} {{ company && company.multicurrency_cashboxes ?
                            findCurrencySymbol(base_currency_id) : currency }}</span>
                        </td>
                      </tr>
                      <tr :class="totalSum() == totalGettingSum() ? 'green' : ''">
                        <th class="text-left text-overline">
                          {{ $t("bookings.accepted") }}
                        </th>
                        <td @click="copyToBuffer(totalGettingSum())" class="font-weight-bold text-right"
                          style="cursor: pointer;">
                          <span>
                            {{ totalGettingSum() }} {{ company && company.multicurrency_cashboxes ?
                              findCurrencySymbol(base_currency_id) : currency }}
                          </span>
                          <v-icon v-if="totalSum() == totalGettingSum()" size="20" class="mx-1">
                            mdi-check-circle-outline
                          </v-icon>
                        </td>
                      </tr>
                    </tbody>
                  </template>
                </v-simple-table>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="4"
            :style="`border-right: 1px solid ${$vuetify.theme.dark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.12)'};`">
            <v-toolbar dense elevation="1">
              <v-toolbar-title>{{ $t("bookings.counts") }}</v-toolbar-title>
            </v-toolbar>
            <v-row>
              <v-col cols="12" class="counts_payment_dialog">
                <v-data-table :headers="counts_headers" :items="booking.counts" :loading="isLoading"
                  :loading-text="$t('custom.loading_table')" :items-per-page="10" mobile-breakpoint="100" sort-by="id"
                  sort-desc dense :footer-props="{
                    pageText: `{0} ${$t('custom.of')} {1}`,
                    itemsPerPageText: '',
                    showFirstLastPage: false,
                    itemsPerPageOptions: [10, 20, 50, 100, -1],
                  }">
                  <template slot="item" slot-scope="props">
                    <tr>
                      <td class="text-center" :key="props.item.id">
                        {{ props.item.id }}
                      </td>
                      <td class="text-center">
                        {{ formatCreatedAt(props.item.created_at) }}
                      </td>
                      <td v-if="company && company.multicurrency_cashboxes" class="text-center">
                        {{ props.item.operation ? "" : "-" }}{{ props.item.sum
                        }}{{ findCurrencySymbol(props.item.currency_id) }}
                      </td>
                      <td v-else class="text-center">
                        {{ props.item.operation ? "" : "-" }}{{ props.item.sum
                        }}{{ currency }}
                      </td>
                      <td class="text-center" v-if="props.item.cash">
                        <v-icon class="text-center" color="success" v-tooltip="{
                          content: $t('money.cash'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                          mdi-cash
                        </v-icon>
                      </td>
                      <td class="text-center" v-if="props.item.cashless">
                        <v-icon class="text-center" color="warning" v-tooltip="{
                          content: $t('money.terminal'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                          mdi-contactless-payment-circle-outline
                        </v-icon>
                      </td>
                      <td class="text-center" v-if="props.item.entity">
                        <v-icon class="text-center" color="secondary" v-tooltip="{
                          content: $t('money.to_entity'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                          mdi-bank
                        </v-icon>
                      </td>
                      <td class="text-center" v-if="props.item.cash_card">
                        <v-icon class="text-center" color="info" v-tooltip="{
                          content: $t('money.card_to_card'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                          mdi-credit-card-check-outline
                        </v-icon>
                      </td>
                      <td class="text-center" v-if="props.item.client_balance">
                        <v-icon class="text-center" color="error" v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                          mdi-account-arrow-right
                        </v-icon>
                      </td>
                      <td class="text-center">
                        {{ translateCountGroup(props.item.group) }}
                      </td>
                    </tr>
                  </template>
                  <template slot="no-data">
                    {{ $t("custom.no_data_in_table") }}
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="12" md="4" class="mx-auto">
            <v-toolbar dense elevation="1">
              <v-toolbar-title>{{ $t("bookings.payment") }}</v-toolbar-title>
            </v-toolbar>
            <!-- Приёмка 29.09: у договора выкупа — что к оплате по графику и быстрые суммы. -->
            <rent-to-own-payment-summary v-if="payment_dialog && rentToOwnActive" :contract="rentToOwnContract"
              class="mt-3" @fill="fillRentFromContract" />
            <!-- FT-193 W1: при неизвестном исходе списания с карты поля сумм, способов и касс заперты и показывают снимок
                 первой отправки — повтор уйдёт им же. readonly формы доходит и до чекбоксов способов со своим :disabled
                 (их disabled перекрывает disabled формы, readonly — нет); своего readonly у полей внутри быть не должно. -->
            <v-form :disabled="outcomeUnknown" :readonly="outcomeUnknown" @submit.prevent data-testid="payment-fields-form">
            <v-row align="center" class="mt-2">
              <v-col auto>
                <v-text-field v-model.number="departure_payments.rent.sum"
                  :label="rentToOwnActive ? $t('rent_to_own.payment.rent_field') : $t('bookings.rent')" dense
                  data-testid="payment-rent-sum"
                  :messages="messageInBaseCurrency(departure_payments.rent.sum, departure_payments.rent.currency_id)"></v-text-field>
              </v-col>
              <v-col auto v-if="company && company.multicurrency_cashboxes">
                <v-select v-model="departure_payments.rent.company_cashbox" :items="active_company_cashboxes"
                  :item-text="item => item.name" :return-object="true"
                  @change="departure_payments.rent.currency_id = departure_payments.rent.company_cashbox.currency_id, departure_payments.rent.company_cashbox_id = departure_payments.rent.company_cashbox.id"
                  :label="$t('companies.currency')" dense data-testid="payment-rent-cashbox"></v-select>
              </v-col>
            </v-row>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.rent.type" color="success" value="Наличные" :disabled="departure_payments.rent.type == 'Терминал' ||
                  departure_payments.rent.type == 'На карту' ||
                  departure_payments.rent.type == 'Р/с' ||
                  departure_payments.rent.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.rent.type == 'Терминал' ||
                    departure_payments.rent.type == 'На карту' ||
                    departure_payments.rent.type == 'Р/с' ||
                    departure_payments.rent.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.rent.type" color="warning" value="Терминал" :disabled="departure_payments.rent.type == 'Наличные' ||
                  departure_payments.rent.type == 'На карту' ||
                  departure_payments.rent.type == 'Р/с' ||
                  departure_payments.rent.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.rent.type == 'Наличные' ||
                    departure_payments.rent.type == 'На карту' ||
                    departure_payments.rent.type == 'Р/с' ||
                    departure_payments.rent.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.rent.type" color="info" value="На карту" :disabled="departure_payments.rent.type == 'Наличные' ||
                  departure_payments.rent.type == 'Терминал' ||
                  departure_payments.rent.type == 'Р/с' ||
                  departure_payments.rent.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.rent.type == 'Наличные' ||
                    departure_payments.rent.type == 'Терминал' ||
                    departure_payments.rent.type == 'Р/с' ||
                    departure_payments.rent.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.rent.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.rent.type == 'Наличные' ||
                    departure_payments.rent.type == 'Терминал' ||
                    departure_payments.rent.type == 'На карту' ||
                    departure_payments.rent.type == 'Баланс клиента'
                    " :style="departure_payments.rent.type == 'Наличные' ||
                      departure_payments.rent.type == 'Терминал' ||
                      departure_payments.rent.type == 'На карту' ||
                      departure_payments.rent.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.rent.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.rent.type == 'Наличные' ||
                    departure_payments.rent.type == 'Терминал' ||
                    departure_payments.rent.type == 'На карту' ||
                    departure_payments.rent.type == 'Р/с'
                    " :style="departure_payments.rent.type == 'Наличные' ||
                      departure_payments.rent.type == 'Терминал' ||
                      departure_payments.rent.type == 'На карту' ||
                      departure_payments.rent.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2"></v-divider>
            <v-flex v-if="currentDeposit() > 0" xs12>
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.deposit.sum" :label="$t('bookings.deposit')" dense
                    :messages="messageInBaseCurrency(departure_payments.deposit.sum, departure_payments.deposit.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.deposit.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.deposit.currency_id = departure_payments.deposit.company_cashbox.currency_id, departure_payments.deposit.company_cashbox_id = departure_payments.deposit.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between" v-if="currentDeposit() > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.deposit.type" color="success" value="Наличные" :disabled="departure_payments.deposit.type == 'Терминал' ||
                  departure_payments.deposit.type == 'На карту' ||
                  departure_payments.deposit.type == 'Р/с' ||
                  departure_payments.deposit.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.deposit.type == 'Терминал' ||
                    departure_payments.deposit.type == 'На карту' ||
                    departure_payments.deposit.type == 'Р/с' ||
                    departure_payments.deposit.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.deposit.type" color="warning" value="Терминал" :disabled="departure_payments.deposit.type == 'Наличные' ||
                  departure_payments.deposit.type == 'На карту' ||
                  departure_payments.deposit.type == 'Р/с' ||
                  departure_payments.deposit.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.deposit.type == 'Наличные' ||
                    departure_payments.deposit.type == 'На карту' ||
                    departure_payments.deposit.type == 'Р/с' ||
                    departure_payments.deposit.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.deposit.type" color="info" value="На карту" :disabled="departure_payments.deposit.type == 'Наличные' ||
                  departure_payments.deposit.type == 'Терминал' ||
                  departure_payments.deposit.type == 'Р/с' ||
                  departure_payments.deposit.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.deposit.type == 'Наличные' ||
                    departure_payments.deposit.type == 'Терминал' ||
                    departure_payments.deposit.type == 'Р/с' ||
                    departure_payments.deposit.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.deposit.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.deposit.type == 'Наличные' ||
                    departure_payments.deposit.type == 'Терминал' ||
                    departure_payments.deposit.type == 'На карту' ||
                    departure_payments.deposit.type == 'Баланс клиента'
                    " :style="departure_payments.deposit.type == 'Наличные' ||
                      departure_payments.deposit.type == 'Терминал' ||
                      departure_payments.deposit.type == 'На карту' ||
                      departure_payments.deposit.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.deposit.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.deposit.type == 'Наличные' ||
                    departure_payments.deposit.type == 'Терминал' ||
                    departure_payments.deposit.type == 'На карту' ||
                    departure_payments.deposit.type == 'Р/с'
                    " :style="departure_payments.deposit.type == 'Наличные' ||
                      departure_payments.deposit.type == 'Терминал' ||
                      departure_payments.deposit.type == 'На карту' ||
                      departure_payments.deposit.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider v-if="showAddPayment || booking.delivery > 0" class="mt-2"></v-divider>
            <v-flex v-if="showAddPayment || booking.delivery > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.delivery.sum" :label="$t('bookings.delivery')" dense
                    :messages="messageInBaseCurrency(departure_payments.delivery.sum, departure_payments.delivery.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.delivery.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.delivery.currency_id = departure_payments.delivery.company_cashbox.currency_id, departure_payments.delivery.company_cashbox_id = departure_payments.delivery.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.delivery > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.delivery.type" color="success" value="Наличные" :disabled="departure_payments.delivery.type == 'Терминал' ||
                  departure_payments.delivery.type == 'На карту' ||
                  departure_payments.delivery.type == 'Р/с' ||
                  departure_payments.delivery.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery.type == 'Терминал' ||
                    departure_payments.delivery.type == 'На карту' ||
                    departure_payments.delivery.type == 'Р/с' ||
                    departure_payments.delivery.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.delivery.type" color="warning" value="Терминал" :disabled="departure_payments.delivery.type == 'Наличные' ||
                  departure_payments.delivery.type == 'На карту' ||
                  departure_payments.delivery.type == 'Р/с' ||
                  departure_payments.delivery.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery.type == 'Наличные' ||
                    departure_payments.delivery.type == 'На карту' ||
                    departure_payments.delivery.type == 'Р/с' ||
                    departure_payments.delivery.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.delivery.type" color="info" value="На карту" :disabled="departure_payments.delivery.type == 'Наличные' ||
                  departure_payments.delivery.type == 'Терминал' ||
                  departure_payments.delivery.type == 'Р/с' ||
                  departure_payments.delivery.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery.type == 'Наличные' ||
                    departure_payments.delivery.type == 'Терминал' ||
                    departure_payments.delivery.type == 'Р/с' ||
                    departure_payments.delivery.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.delivery.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.delivery.type == 'Наличные' ||
                    departure_payments.delivery.type == 'Терминал' ||
                    departure_payments.delivery.type == 'На карту' ||
                    departure_payments.delivery.type == 'Баланс клиента'
                    " :style="departure_payments.delivery.type == 'Наличные' ||
                      departure_payments.delivery.type == 'Терминал' ||
                      departure_payments.delivery.type == 'На карту' ||
                      departure_payments.delivery.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.delivery.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.delivery.type == 'Наличные' ||
                    departure_payments.delivery.type == 'Терминал' ||
                    departure_payments.delivery.type == 'На карту' ||
                    departure_payments.delivery.type == 'Р/с'
                    " :style="departure_payments.delivery.type == 'Наличные' ||
                      departure_payments.delivery.type == 'Терминал' ||
                      departure_payments.delivery.type == 'На карту' ||
                      departure_payments.delivery.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.delivery_end > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.delivery_end > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.delivery_end.sum" :label="$t('bookings.checkout')"
                    dense
                    :messages="messageInBaseCurrency(departure_payments.delivery_end.sum, departure_payments.delivery_end.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.delivery_end.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.delivery_end.currency_id = departure_payments.delivery_end.company_cashbox.currency_id, departure_payments.delivery_end.company_cashbox_id = departure_payments.delivery_end.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.delivery_end > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.delivery_end.type" color="success" value="Наличные" :disabled="departure_payments.delivery_end.type == 'Терминал' ||
                  departure_payments.delivery_end.type == 'На карту' ||
                  departure_payments.delivery_end.type == 'Р/с' ||
                  departure_payments.delivery_end.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery_end.type == 'Терминал' ||
                    departure_payments.delivery_end.type == 'На карту' ||
                    departure_payments.delivery_end.type == 'Р/с' ||
                    departure_payments.delivery_end.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.delivery_end.type" color="warning" value="Терминал" :disabled="departure_payments.delivery_end.type == 'Наличные' ||
                  departure_payments.delivery_end.type == 'На карту' ||
                  departure_payments.delivery_end.type == 'Р/с' ||
                  departure_payments.delivery_end.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery_end.type == 'Наличные' ||
                    departure_payments.delivery_end.type == 'На карту' ||
                    departure_payments.delivery_end.type == 'Р/с' ||
                    departure_payments.delivery_end.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.delivery_end.type" color="info" value="На карту" :disabled="departure_payments.delivery_end.type == 'Наличные' ||
                  departure_payments.delivery_end.type == 'Терминал' ||
                  departure_payments.delivery_end.type == 'Р/с' ||
                  departure_payments.delivery_end.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.delivery_end.type == 'Наличные' ||
                    departure_payments.delivery_end.type == 'Терминал' ||
                    departure_payments.delivery_end.type == 'Р/с' ||
                    departure_payments.delivery_end.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.delivery_end.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.delivery_end.type == 'Наличные' ||
                    departure_payments.delivery_end.type == 'Терминал' ||
                    departure_payments.delivery_end.type == 'На карту' ||
                    departure_payments.delivery_end.type == 'Баланс клиента'
                    " :style="departure_payments.delivery_end.type == 'Наличные' ||
                      departure_payments.delivery_end.type == 'Терминал' ||
                      departure_payments.delivery_end.type == 'На карту' ||
                      departure_payments.delivery_end.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.delivery_end.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.delivery_end.type == 'Наличные' ||
                    departure_payments.delivery_end.type == 'Терминал' ||
                    departure_payments.delivery_end.type == 'На карту' ||
                    departure_payments.delivery_end.type == 'Р/с'
                    " :style="departure_payments.delivery_end.type == 'Наличные' ||
                      departure_payments.delivery_end.type == 'Терминал' ||
                      departure_payments.delivery_end.type == 'На карту' ||
                      departure_payments.delivery_end.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2"
              v-if="showAddPayment || booking.start_clean_payment > 0 || booking.clean_payment > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.start_clean_payment > 0 || booking.clean_payment > 0" xs12
              class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.clean.sum" :label="$t('bookings.clean')" dense
                    :messages="messageInBaseCurrency(departure_payments.clean.sum, departure_payments.clean.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.clean.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.clean.currency_id = departure_payments.clean.company_cashbox.currency_id, departure_payments.clean.company_cashbox_id = departure_payments.clean.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between" v-if="showAddPayment ||
              booking.start_clean_payment > 0 ||
              booking.clean_payment > 0
            ">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.clean.type" color="success" value="Наличные" :disabled="departure_payments.clean.type == 'Терминал' ||
                  departure_payments.clean.type == 'На карту' ||
                  departure_payments.clean.type == 'Р/с' ||
                  departure_payments.clean.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.clean.type == 'Терминал' ||
                    departure_payments.clean.type == 'На карту' ||
                    departure_payments.clean.type == 'Р/с' ||
                    departure_payments.clean.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.clean.type" color="warning" value="Терминал" :disabled="departure_payments.clean.type == 'Наличные' ||
                  departure_payments.clean.type == 'На карту' ||
                  departure_payments.clean.type == 'Р/с' ||
                  departure_payments.clean.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.clean.type == 'Наличные' ||
                    departure_payments.clean.type == 'На карту' ||
                    departure_payments.clean.type == 'Р/с' ||
                    departure_payments.clean.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.clean.type" color="info" value="На карту" :disabled="departure_payments.clean.type == 'Наличные' ||
                  departure_payments.clean.type == 'Терминал' ||
                  departure_payments.clean.type == 'Р/с' ||
                  departure_payments.clean.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.clean.type == 'Наличные' ||
                    departure_payments.clean.type == 'Терминал' ||
                    departure_payments.clean.type == 'Р/с' ||
                    departure_payments.clean.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.clean.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.clean.type == 'Наличные' ||
                    departure_payments.clean.type == 'Терминал' ||
                    departure_payments.clean.type == 'На карту' ||
                    departure_payments.clean.type == 'Баланс клиента'
                    " :style="departure_payments.clean.type == 'Наличные' ||
                      departure_payments.clean.type == 'Терминал' ||
                      departure_payments.clean.type == 'На карту' ||
                      departure_payments.clean.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.clean.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.clean.type == 'Наличные' ||
                    departure_payments.clean.type == 'Терминал' ||
                    departure_payments.clean.type == 'На карту' ||
                    departure_payments.clean.type == 'Р/с'
                    " :style="departure_payments.clean.type == 'Наличные' ||
                      departure_payments.clean.type == 'Терминал' ||
                      departure_payments.clean.type == 'На карту' ||
                      departure_payments.clean.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.hours_cost > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.hours_cost > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.hours_cost.sum"
                    :label="$t('bookings.add_hours_short')" dense
                    :messages="messageInBaseCurrency(departure_payments.hours_cost.sum, departure_payments.hours_cost.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.hours_cost.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.hours_cost.currency_id = departure_payments.hours_cost.company_cashbox.currency_id, departure_payments.hours_cost.company_cashbox_id = departure_payments.hours_cost.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.hours_cost > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.hours_cost.type" color="success" value="Наличные" :disabled="departure_payments.hours_cost.type == 'Терминал' ||
                  departure_payments.hours_cost.type == 'На карту' ||
                  departure_payments.hours_cost.type == 'Р/с' ||
                  departure_payments.hours_cost.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.hours_cost.type == 'Терминал' ||
                    departure_payments.hours_cost.type == 'На карту' ||
                    departure_payments.hours_cost.type == 'Р/с' ||
                    departure_payments.hours_cost.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.hours_cost.type" color="warning" value="Терминал" :disabled="departure_payments.hours_cost.type == 'Наличные' ||
                  departure_payments.hours_cost.type == 'На карту' ||
                  departure_payments.hours_cost.type == 'Р/с' ||
                  departure_payments.hours_cost.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.hours_cost.type == 'Наличные' ||
                    departure_payments.hours_cost.type == 'На карту' ||
                    departure_payments.hours_cost.type == 'Р/с' ||
                    departure_payments.hours_cost.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.hours_cost.type" color="info" value="На карту" :disabled="departure_payments.hours_cost.type == 'Наличные' ||
                  departure_payments.hours_cost.type == 'Терминал' ||
                  departure_payments.hours_cost.type == 'Р/с' ||
                  departure_payments.hours_cost.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.hours_cost.type == 'Наличные' ||
                    departure_payments.hours_cost.type == 'Терминал' ||
                    departure_payments.hours_cost.type == 'Р/с' ||
                    departure_payments.hours_cost.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.hours_cost.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.hours_cost.type == 'Наличные' ||
                    departure_payments.hours_cost.type == 'Терминал' ||
                    departure_payments.hours_cost.type == 'На карту' ||
                    departure_payments.hours_cost.type == 'Баланс клиента'
                    " :style="departure_payments.hours_cost.type == 'Наличные' ||
                      departure_payments.hours_cost.type == 'Терминал' ||
                      departure_payments.hours_cost.type == 'На карту' ||
                      departure_payments.hours_cost.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.hours_cost.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.hours_cost.type == 'Наличные' ||
                    departure_payments.hours_cost.type == 'Терминал' ||
                    departure_payments.hours_cost.type == 'На карту' ||
                    departure_payments.hours_cost.type == 'Р/с'
                    " :style="departure_payments.hours_cost.type == 'Наличные' ||
                      departure_payments.hours_cost.type == 'Терминал' ||
                      departure_payments.hours_cost.type == 'На карту' ||
                      departure_payments.hours_cost.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="(showAddPayment && showInsurance) || booking.insurance > 0"></v-divider>
            <v-flex v-if="(showAddPayment && showInsurance) || booking.insurance > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.insurance.sum" :label="$t('bookings.insurance')"
                    dense
                    :messages="messageInBaseCurrency(departure_payments.insurance.sum, departure_payments.insurance.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.insurance.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.insurance.currency_id = departure_payments.insurance.company_cashbox.currency_id, departure_payments.insurance.company_cashbox_id = departure_payments.insurance.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="(showAddPayment && showInsurance) || booking.insurance > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.insurance.type" color="success" value="Наличные" :disabled="departure_payments.insurance.type == 'Терминал' ||
                  departure_payments.insurance.type == 'На карту' ||
                  departure_payments.insurance.type == 'Р/с' ||
                  departure_payments.insurance.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.insurance.type == 'Терминал' ||
                    departure_payments.insurance.type == 'На карту' ||
                    departure_payments.insurance.type == 'Р/с' ||
                    departure_payments.insurance.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.insurance.type" color="warning" value="Терминал" :disabled="departure_payments.insurance.type == 'Наличные' ||
                  departure_payments.insurance.type == 'На карту' ||
                  departure_payments.insurance.type == 'Р/с' ||
                  departure_payments.insurance.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.insurance.type == 'Наличные' ||
                    departure_payments.insurance.type == 'На карту' ||
                    departure_payments.insurance.type == 'Р/с' ||
                    departure_payments.insurance.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.insurance.type" color="info" value="На карту" :disabled="departure_payments.insurance.type == 'Наличные' ||
                  departure_payments.insurance.type == 'Терминал' ||
                  departure_payments.insurance.type == 'Р/с' ||
                  departure_payments.insurance.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.insurance.type == 'Наличные' ||
                    departure_payments.insurance.type == 'Терминал' ||
                    departure_payments.insurance.type == 'Р/с' ||
                    departure_payments.insurance.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.insurance.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.insurance.type == 'Наличные' ||
                    departure_payments.insurance.type == 'Терминал' ||
                    departure_payments.insurance.type == 'На карту' ||
                    departure_payments.insurance.type == 'Баланс клиента'
                    " :style="departure_payments.insurance.type == 'Наличные' ||
                      departure_payments.insurance.type == 'Терминал' ||
                      departure_payments.insurance.type == 'На карту' ||
                      departure_payments.insurance.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.insurance.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.insurance.type == 'Наличные' ||
                    departure_payments.insurance.type == 'Терминал' ||
                    departure_payments.insurance.type == 'На карту' ||
                    departure_payments.insurance.type == 'Р/с'
                    " :style="departure_payments.insurance.type == 'Наличные' ||
                      departure_payments.insurance.type == 'Терминал' ||
                      departure_payments.insurance.type == 'На карту' ||
                      departure_payments.insurance.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.equipment > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.equipment > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <!-- UAT 2026-08-07: легаси-поле и каталожные допы FT-102 шли
                       под одним лейблом «Оборудование» — оператор не различал,
                       куда вводить сумму. Легаси-строка принимает внекаталожные
                       суммы, поэтому «Другое оборудование». -->
                  <v-text-field v-model.number="departure_payments.equipment.sum" :label="$t('bookings.other_equipment')"
                    dense
                    :messages="messageInBaseCurrency(departure_payments.equipment.sum, departure_payments.equipment.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.equipment.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.equipment.currency_id = departure_payments.equipment.company_cashbox.currency_id, departure_payments.equipment.company_cashbox_id = departure_payments.equipment.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.equipment > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.equipment.type" color="success" value="Наличные" :disabled="departure_payments.equipment.type == 'Терминал' ||
                  departure_payments.equipment.type == 'На карту' ||
                  departure_payments.equipment.type == 'Р/с' ||
                  departure_payments.equipment.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.equipment.type == 'Терминал' ||
                    departure_payments.equipment.type == 'На карту' ||
                    departure_payments.equipment.type == 'Р/с' ||
                    departure_payments.equipment.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.equipment.type" color="warning" value="Терминал" :disabled="departure_payments.equipment.type == 'Наличные' ||
                  departure_payments.equipment.type == 'На карту' ||
                  departure_payments.equipment.type == 'Р/с' ||
                  departure_payments.equipment.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.equipment.type == 'Наличные' ||
                    departure_payments.equipment.type == 'На карту' ||
                    departure_payments.equipment.type == 'Р/с' ||
                    departure_payments.equipment.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.equipment.type" color="info" value="На карту" :disabled="departure_payments.equipment.type == 'Наличные' ||
                  departure_payments.equipment.type == 'Терминал' ||
                  departure_payments.equipment.type == 'Р/с' ||
                  departure_payments.equipment.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.equipment.type == 'Наличные' ||
                    departure_payments.equipment.type == 'Терминал' ||
                    departure_payments.equipment.type == 'Р/с' ||
                    departure_payments.equipment.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.equipment.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.equipment.type == 'Наличные' ||
                    departure_payments.equipment.type == 'Терминал' ||
                    departure_payments.equipment.type == 'На карту' ||
                    departure_payments.equipment.type == 'Баланс клиента'
                    " :style="departure_payments.equipment.type == 'Наличные' ||
                      departure_payments.equipment.type == 'Терминал' ||
                      departure_payments.equipment.type == 'На карту' ||
                      departure_payments.equipment.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.equipment.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.equipment.type == 'Наличные' ||
                    departure_payments.equipment.type == 'Терминал' ||
                    departure_payments.equipment.type == 'На карту' ||
                    departure_payments.equipment.type == 'Р/с'
                    " :style="departure_payments.equipment.type == 'Наличные' ||
                      departure_payments.equipment.type == 'Терминал' ||
                      departure_payments.equipment.type == 'На карту' ||
                      departure_payments.equipment.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <!-- FT-102: категория оплаты каталожного оборудования (авто-видима при addons_cost > 0) -->
            <v-divider class="mt-2" v-if="showAddPayment || booking.addons_cost > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.addons_cost > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.addons.sum" :label="$t('booking_addons.title')"
                    dense
                    :messages="messageInBaseCurrency(departure_payments.addons.sum, departure_payments.addons.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.addons.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.addons.currency_id = departure_payments.addons.company_cashbox.currency_id, departure_payments.addons.company_cashbox_id = departure_payments.addons.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.addons_cost > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.addons.type" color="success" value="Наличные" :disabled="departure_payments.addons.type == 'Терминал' ||
                  departure_payments.addons.type == 'На карту' ||
                  departure_payments.addons.type == 'Р/с' ||
                  departure_payments.addons.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.addons.type == 'Терминал' ||
                    departure_payments.addons.type == 'На карту' ||
                    departure_payments.addons.type == 'Р/с' ||
                    departure_payments.addons.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.addons.type" color="warning" value="Терминал" :disabled="departure_payments.addons.type == 'Наличные' ||
                  departure_payments.addons.type == 'На карту' ||
                  departure_payments.addons.type == 'Р/с' ||
                  departure_payments.addons.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.addons.type == 'Наличные' ||
                    departure_payments.addons.type == 'На карту' ||
                    departure_payments.addons.type == 'Р/с' ||
                    departure_payments.addons.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.addons.type" color="info" value="На карту" :disabled="departure_payments.addons.type == 'Наличные' ||
                  departure_payments.addons.type == 'Терминал' ||
                  departure_payments.addons.type == 'Р/с' ||
                  departure_payments.addons.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.addons.type == 'Наличные' ||
                    departure_payments.addons.type == 'Терминал' ||
                    departure_payments.addons.type == 'Р/с' ||
                    departure_payments.addons.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.addons.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.addons.type == 'Наличные' ||
                    departure_payments.addons.type == 'Терминал' ||
                    departure_payments.addons.type == 'На карту' ||
                    departure_payments.addons.type == 'Баланс клиента'
                    " :style="departure_payments.addons.type == 'Наличные' ||
                      departure_payments.addons.type == 'Терминал' ||
                      departure_payments.addons.type == 'На карту' ||
                      departure_payments.addons.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.addons.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.addons.type == 'Наличные' ||
                    departure_payments.addons.type == 'Терминал' ||
                    departure_payments.addons.type == 'На карту' ||
                    departure_payments.addons.type == 'Р/с'
                    " :style="departure_payments.addons.type == 'Наличные' ||
                      departure_payments.addons.type == 'Терминал' ||
                      departure_payments.addons.type == 'На карту' ||
                      departure_payments.addons.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="(showAddPayment && showFines) || booking.fine > 0"></v-divider>
            <v-flex v-if="(showAddPayment && showFines) || booking.fine > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.fine.sum" :label="$t('bookings.fines')" dense
                    :messages="messageInBaseCurrency(departure_payments.fine.sum, departure_payments.fine.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.fine.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.fine.currency_id = departure_payments.fine.company_cashbox.currency_id, departure_payments.fine.company_cashbox_id = departure_payments.fine.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="(showAddPayment && showFines) || booking.fine > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.fine.type" color="success" value="Наличные" :disabled="departure_payments.fine.type == 'Терминал' ||
                  departure_payments.fine.type == 'На карту' ||
                  departure_payments.fine.type == 'Р/с' ||
                  departure_payments.fine.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.fine.type == 'Терминал' ||
                    departure_payments.fine.type == 'На карту' ||
                    departure_payments.fine.type == 'Р/с' ||
                    departure_payments.fine.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.fine.type" color="warning" value="Терминал" :disabled="departure_payments.fine.type == 'Наличные' ||
                  departure_payments.fine.type == 'На карту' ||
                  departure_payments.fine.type == 'Р/с' ||
                  departure_payments.fine.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.fine.type == 'Наличные' ||
                    departure_payments.fine.type == 'На карту' ||
                    departure_payments.fine.type == 'Р/с' ||
                    departure_payments.fine.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.fine.type" color="info" value="На карту" :disabled="departure_payments.fine.type == 'Наличные' ||
                  departure_payments.fine.type == 'Терминал' ||
                  departure_payments.fine.type == 'Р/с' ||
                  departure_payments.fine.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.fine.type == 'Наличные' ||
                    departure_payments.fine.type == 'Терминал' ||
                    departure_payments.fine.type == 'Р/с' ||
                    departure_payments.fine.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.fine.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.fine.type == 'Наличные' ||
                    departure_payments.fine.type == 'Терминал' ||
                    departure_payments.fine.type == 'На карту' ||
                    departure_payments.fine.type == 'Баланс клиента'
                    " :style="departure_payments.fine.type == 'Наличные' ||
                      departure_payments.fine.type == 'Терминал' ||
                      departure_payments.fine.type == 'На карту' ||
                      departure_payments.fine.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.fine.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.fine.type == 'Наличные' ||
                    departure_payments.fine.type == 'Терминал' ||
                    departure_payments.fine.type == 'На карту' ||
                    departure_payments.fine.type == 'Р/с'
                    " :style="departure_payments.fine.type == 'Наличные' ||
                      departure_payments.fine.type == 'Терминал' ||
                      departure_payments.fine.type == 'На карту' ||
                      departure_payments.fine.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.add_drivers_cost > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.add_drivers_cost > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.add_drivers_cost.sum"
                    :label="$t('bookings.add_drivers_small')" dense
                    :messages="messageInBaseCurrency(departure_payments.add_drivers_cost.sum, departure_payments.add_drivers_cost.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.add_drivers_cost.company_cashbox"
                    :items="active_company_cashboxes" :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.add_drivers_cost.currency_id = departure_payments.add_drivers_cost.company_cashbox.currency_id, departure_payments.add_drivers_cost.company_cashbox_id = departure_payments.add_drivers_cost.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.add_drivers_cost > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.add_drivers_cost.type" color="success" value="Наличные"
                  :disabled="departure_payments.add_drivers_cost.type == 'Терминал' ||
                    departure_payments.add_drivers_cost.type == 'На карту' ||
                    departure_payments.add_drivers_cost.type == 'Р/с' ||
                    departure_payments.add_drivers_cost.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="departure_payments.add_drivers_cost.type == 'Терминал' ||
                      departure_payments.add_drivers_cost.type == 'На карту' ||
                      departure_payments.add_drivers_cost.type == 'Р/с' ||
                      departure_payments.add_drivers_cost.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.add_drivers_cost.type" color="warning" value="Терминал"
                  :disabled="departure_payments.add_drivers_cost.type == 'Наличные' ||
                    departure_payments.add_drivers_cost.type == 'На карту' ||
                    departure_payments.add_drivers_cost.type == 'Р/с' ||
                    departure_payments.add_drivers_cost.type == 'Баланс клиента'
                    " class="mt-0 mr-0" dense hide-details :style="departure_payments.add_drivers_cost.type == 'Наличные' ||
                      departure_payments.add_drivers_cost.type == 'На карту' ||
                      departure_payments.add_drivers_cost.type == 'Р/с' ||
                      departure_payments.add_drivers_cost.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.add_drivers_cost.type" color="info" value="На карту" :disabled="departure_payments.add_drivers_cost.type == 'Наличные' ||
                  departure_payments.add_drivers_cost.type == 'Терминал' ||
                  departure_payments.add_drivers_cost.type == 'Р/с' ||
                  departure_payments.add_drivers_cost.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.add_drivers_cost.type == 'Наличные' ||
                    departure_payments.add_drivers_cost.type == 'Терминал' ||
                    departure_payments.add_drivers_cost.type == 'Р/с' ||
                    departure_payments.add_drivers_cost.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.add_drivers_cost.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.add_drivers_cost.type == 'Наличные' ||
                    departure_payments.add_drivers_cost.type == 'Терминал' ||
                    departure_payments.add_drivers_cost.type == 'На карту' ||
                    departure_payments.add_drivers_cost.type == 'Баланс клиента'
                    " :style="departure_payments.add_drivers_cost.type == 'Наличные' ||
                      departure_payments.add_drivers_cost.type == 'Терминал' ||
                      departure_payments.add_drivers_cost.type == 'На карту' ||
                      departure_payments.add_drivers_cost.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.add_drivers_cost.type" color="error" class="mt-0 mr-0" dense
                  hide-details value="Баланс клиента" :disabled="departure_payments.add_drivers_cost.type == 'Наличные' ||
                    departure_payments.add_drivers_cost.type == 'Терминал' ||
                    departure_payments.add_drivers_cost.type == 'На карту' ||
                    departure_payments.add_drivers_cost.type == 'Р/с'
                    " :style="departure_payments.add_drivers_cost.type == 'Наличные' ||
                      departure_payments.add_drivers_cost.type == 'Терминал' ||
                      departure_payments.add_drivers_cost.type == 'На карту' ||
                      departure_payments.add_drivers_cost.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="(showAddPayment && showFuel) || booking.gas > 0"></v-divider>
            <!-- Топливо -->
            <v-flex v-if="(showAddPayment && showFuel) || booking.gas > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.gas.sum" :label="$t('add_services.fuel')" dense
                    :messages="messageInBaseCurrency(departure_payments.gas.sum, departure_payments.gas.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.gas.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.gas.currency_id = departure_payments.gas.company_cashbox.currency_id, departure_payments.gas.company_cashbox_id = departure_payments.gas.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="(showAddPayment && showFuel) || booking.gas > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.gas.type" color="success" value="Наличные" :disabled="departure_payments.gas.type == 'Терминал' ||
                  departure_payments.gas.type == 'На карту' ||
                  departure_payments.gas.type == 'Р/с' ||
                  departure_payments.gas.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.gas.type == 'Терминал' ||
                    departure_payments.gas.type == 'На карту' ||
                    departure_payments.gas.type == 'Р/с' ||
                    departure_payments.gas.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.gas.type" color="warning" value="Терминал" :disabled="departure_payments.gas.type == 'Наличные' ||
                  departure_payments.gas.type == 'На карту' ||
                  departure_payments.gas.type == 'Р/с' ||
                  departure_payments.gas.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.gas.type == 'Наличные' ||
                    departure_payments.gas.type == 'На карту' ||
                    departure_payments.gas.type == 'Р/с' ||
                    departure_payments.gas.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.gas.type" color="info" value="На карту" :disabled="departure_payments.gas.type == 'Наличные' ||
                  departure_payments.gas.type == 'Терминал' ||
                  departure_payments.gas.type == 'Р/с' ||
                  departure_payments.gas.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.gas.type == 'Наличные' ||
                    departure_payments.gas.type == 'Терминал' ||
                    departure_payments.gas.type == 'Р/с' ||
                    departure_payments.gas.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.gas.type" color="secondary" class="mt-0 mr-0" dense hide-details
                  value="Р/с" :disabled="departure_payments.gas.type == 'Наличные' ||
                    departure_payments.gas.type == 'Терминал' ||
                    departure_payments.gas.type == 'На карту' ||
                    departure_payments.gas.type == 'Баланс клиента'
                    " :style="departure_payments.gas.type == 'Наличные' ||
                      departure_payments.gas.type == 'Терминал' ||
                      departure_payments.gas.type == 'На карту' ||
                      departure_payments.gas.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.gas.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.gas.type == 'Наличные' ||
                    departure_payments.gas.type == 'Терминал' ||
                    departure_payments.gas.type == 'На карту' ||
                    departure_payments.gas.type == 'Р/с'
                    " :style="departure_payments.gas.type == 'Наличные' ||
                      departure_payments.gas.type == 'Терминал' ||
                      departure_payments.gas.type == 'На карту' ||
                      departure_payments.gas.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <!-- Повреждения -->
            <v-flex v-if="showAddPayment || booking.damage > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.damage.sum" :label="$t('add_services.damages')" dense
                    :messages="messageInBaseCurrency(departure_payments.damage.sum, departure_payments.damage.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.damage.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.damage.currency_id = departure_payments.damage.company_cashbox.currency_id, departure_payments.damage.company_cashbox_id = departure_payments.damage.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.damage > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.damage.type" color="success" value="Наличные" :disabled="departure_payments.damage.type == 'Терминал' ||
                  departure_payments.damage.type == 'На карту' ||
                  departure_payments.damage.type == 'Р/с' ||
                  departure_payments.damage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.damage.type == 'Терминал' ||
                    departure_payments.damage.type == 'На карту' ||
                    departure_payments.damage.type == 'Р/с' ||
                    departure_payments.damage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.damage.type" color="warning" value="Терминал" :disabled="departure_payments.damage.type == 'Наличные' ||
                  departure_payments.damage.type == 'На карту' ||
                  departure_payments.damage.type == 'Р/с' ||
                  departure_payments.damage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.damage.type == 'Наличные' ||
                    departure_payments.damage.type == 'На карту' ||
                    departure_payments.damage.type == 'Р/с' ||
                    departure_payments.damage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.damage.type" color="info" value="На карту" :disabled="departure_payments.damage.type == 'Наличные' ||
                  departure_payments.damage.type == 'Терминал' ||
                  departure_payments.damage.type == 'Р/с' ||
                  departure_payments.damage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.gas.type == 'Наличные' ||
                    departure_payments.damage.type == 'Терминал' ||
                    departure_payments.damage.type == 'Р/с' ||
                    departure_payments.damage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.damage.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.damage.type == 'Наличные' ||
                    departure_payments.damage.type == 'Терминал' ||
                    departure_payments.damage.type == 'На карту' ||
                    departure_payments.damage.type == 'Баланс клиента'
                    " :style="departure_payments.damage.type == 'Наличные' ||
                      departure_payments.damage.type == 'Терминал' ||
                      departure_payments.damage.type == 'На карту' ||
                      departure_payments.damage.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.damage.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.damage.type == 'Наличные' ||
                    departure_payments.damage.type == 'Терминал' ||
                    departure_payments.damage.type == 'На карту' ||
                    departure_payments.damage.type == 'Р/с'
                    " :style="departure_payments.damage.type == 'Наличные' ||
                      departure_payments.damage.type == 'Терминал' ||
                      departure_payments.damage.type == 'На карту' ||
                      departure_payments.damage.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.damage > 0"></v-divider>
            <!-- Перепробег -->
            <v-flex v-if="(showAddPayment && showMileage) || booking.mileage_cost > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.mileage.sum"
                    :label="$t('add_services.extra_mileage')" dense
                    :messages="messageInBaseCurrency(departure_payments.mileage.sum, departure_payments.mileage.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.mileage.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.mileage.currency_id = departure_payments.mileage.company_cashbox.currency_id, departure_payments.mileage.company_cashbox_id = departure_payments.mileage.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="(showAddPayment && showMileage) || booking.mileage_cost > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.mileage.type" color="success" value="Наличные" :disabled="departure_payments.mileage.type == 'Терминал' ||
                  departure_payments.mileage.type == 'На карту' ||
                  departure_payments.mileage.type == 'Р/с' ||
                  departure_payments.mileage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.mileage.type == 'Терминал' ||
                    departure_payments.mileage.type == 'На карту' ||
                    departure_payments.mileage.type == 'Р/с' ||
                    departure_payments.mileage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.mileage.type" color="warning" value="Терминал" :disabled="departure_payments.mileage.type == 'Наличные' ||
                  departure_payments.mileage.type == 'На карту' ||
                  departure_payments.mileage.type == 'Р/с' ||
                  departure_payments.mileage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.mileage.type == 'Наличные' ||
                    departure_payments.mileage.type == 'На карту' ||
                    departure_payments.mileage.type == 'Р/с' ||
                    departure_payments.mileage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.mileage.type" color="info" value="На карту" :disabled="departure_payments.mileage.type == 'Наличные' ||
                  departure_payments.mileage.type == 'Терминал' ||
                  departure_payments.mileage.type == 'Р/с' ||
                  departure_payments.mileage.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.mileage.type == 'Наличные' ||
                    departure_payments.mileage.type == 'Терминал' ||
                    departure_payments.mileage.type == 'Р/с' ||
                    departure_payments.mileage.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.mileage.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.mileage.type == 'Наличные' ||
                    departure_payments.mileage.type == 'Терминал' ||
                    departure_payments.mileage.type == 'На карту' ||
                    departure_payments.mileage.type == 'Баланс клиента'
                    " :style="departure_payments.mileage.type == 'Наличные' ||
                      departure_payments.mileage.type == 'Терминал' ||
                      departure_payments.mileage.type == 'На карту' ||
                      departure_payments.mileage.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.mileage.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.mileage.type == 'Наличные' ||
                    departure_payments.mileage.type == 'Терминал' ||
                    departure_payments.mileage.type == 'На карту' ||
                    departure_payments.mileage.type == 'Р/с'
                    " :style="departure_payments.mileage.type == 'Наличные' ||
                      departure_payments.mileage.type == 'Терминал' ||
                      departure_payments.mileage.type == 'На карту' ||
                      departure_payments.mileage.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            <v-divider class="mt-2" v-if="showAddPayment || booking.other > 0"></v-divider>
            <v-flex v-if="showAddPayment || booking.other > 0" xs12 class="mt-3">
              <v-row align="center" class="mt-2">
                <v-col auto>
                  <v-text-field v-model.number="departure_payments.other.sum" :label="$t('bookings.other')" dense
                    :messages="messageInBaseCurrency(departure_payments.other.sum, departure_payments.other.currency_id)"></v-text-field>
                </v-col>
                <v-col auto v-if="company && company.multicurrency_cashboxes">
                  <v-select v-model="departure_payments.other.company_cashbox" :items="active_company_cashboxes"
                    :item-text="item => item.name" :return-object="true"
                    @change="departure_payments.other.currency_id = departure_payments.other.company_cashbox.currency_id, departure_payments.other.company_cashbox_id = departure_payments.other.company_cashbox.id"
                    :label="$t('companies.currency')" dense></v-select>
                </v-col>
              </v-row>
            </v-flex>
            <v-flex xs12 sm12 md12 class="d-flex align-center justify-space-between"
              v-if="showAddPayment || booking.other > 0">
              <v-flex v-if="company && company.cash_payment">
                <v-checkbox v-model="departure_payments.other.type" color="success" value="Наличные" :disabled="departure_payments.other.type == 'Терминал' ||
                  departure_payments.other.type == 'На карту' ||
                  departure_payments.other.type == 'Р/с' ||
                  departure_payments.other.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.other.type == 'Терминал' ||
                    departure_payments.other.type == 'На карту' ||
                    departure_payments.other.type == 'Р/с' ||
                    departure_payments.other.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="success" size="30" v-tooltip="{
                      content: $t('tooltips.cash_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-cash
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.cashless_payment">
                <v-checkbox v-model="departure_payments.other.type" color="warning" value="Терминал" :disabled="departure_payments.other.type == 'Наличные' ||
                  departure_payments.other.type == 'На карту' ||
                  departure_payments.other.type == 'Р/с' ||
                  departure_payments.other.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.other.type == 'Наличные' ||
                    departure_payments.other.type == 'На карту' ||
                    departure_payments.other.type == 'Р/с' ||
                    departure_payments.other.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="warning" v-tooltip="{
                      content: $t('tooltips.terminal_count'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-contactless-payment-circle-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.card_to_card_payment">
                <v-checkbox v-model="departure_payments.other.type" color="info" value="На карту" :disabled="departure_payments.other.type == 'Наличные' ||
                  departure_payments.other.type == 'Терминал' ||
                  departure_payments.other.type == 'Р/с' ||
                  departure_payments.other.type == 'Баланс клиента'
                  " class="mt-0 mr-0" dense hide-details :style="departure_payments.other.type == 'Наличные' ||
                    departure_payments.other.type == 'Терминал' ||
                    departure_payments.other.type == 'Р/с' ||
                    departure_payments.other.type == 'Баланс клиента'
                    ? 'opacity: 0.4;'
                    : ''
                    ">
                  <template v-slot:label>
                    <v-icon class="text-center mr-2" color="info" v-tooltip="{
                      content: $t('money.card_to_card'),
                      placement: 'bottom-center',
                      classes: ['info'],
                      targetClasses: ['it-has-a-tooltip'],
                      delay: {
                        show: 500,
                        hide: 500,
                      },
                    }">
                      mdi-credit-card-check-outline
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.entity_payment">
                <v-checkbox v-model="departure_payments.other.type" color="secondary" class="mt-0 mr-0" dense
                  hide-details value="Р/с" :disabled="departure_payments.other.type == 'Наличные' ||
                    departure_payments.other.type == 'Терминал' ||
                    departure_payments.other.type == 'На карту' ||
                    departure_payments.other.type == 'Баланс клиента'
                    " :style="departure_payments.other.type == 'Наличные' ||
                      departure_payments.other.type == 'Терминал' ||
                      departure_payments.other.type == 'На карту' ||
                      departure_payments.other.type == 'Баланс клиента'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('tooltips.entity_count'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="secondary">
                      mdi-bank
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
              <v-flex v-if="company && company.client_payment">
                <v-checkbox v-model="departure_payments.other.type" color="error" class="mt-0 mr-0" dense hide-details
                  value="Баланс клиента" :disabled="departure_payments.other.type == 'Наличные' ||
                    departure_payments.other.type == 'Терминал' ||
                    departure_payments.other.type == 'На карту' ||
                    departure_payments.other.type == 'Р/с'
                    " :style="departure_payments.other.type == 'Наличные' ||
                      departure_payments.other.type == 'Терминал' ||
                      departure_payments.other.type == 'На карту' ||
                      departure_payments.other.type == 'Р/с'
                      ? 'opacity: 0.4;'
                      : ''
                      " v-tooltip="{
                          content: $t('money.client_balance'),
                          placement: 'bottom-center',
                          classes: ['info'],
                          targetClasses: ['it-has-a-tooltip'],
                          delay: {
                            show: 500,
                            hide: 500,
                          },
                        }">
                  <template v-slot:label>
                    <v-icon class="text-center" color="error">
                      mdi-account-arrow-right
                    </v-icon>
                  </template>
                </v-checkbox>
              </v-flex>
            </v-flex>
            </v-form>
            <v-flex md12 v-if="!showAddPayment" class="text-center">
              <v-switch v-model="showAddPayment" :label="$t('bookings.add_services_short')"></v-switch>
              <!-- FT-198 REQ-04: закрывает удержание залога после сохранения платежа; только сотрудникам.
                   FT-193 W1: при неизвестном исходе списания с карты скрыт — повтор закрывает удержание только если об
                   этом просила первая отправка (решение хранится в её снимке), переключатель на повтор не влиял бы. -->
              <v-switch v-if="canReturnDeposit && !outcomeUnknown" v-model="partReturnDeposit"
                :label="$t('bookings.give_full_deposit')" data-testid="give-full-deposit-switch"></v-switch>
            </v-flex>
            <!-- FT-193 REQ-11: списание с привязанной карты — по умолчанию выключено, показывается только когда
                 компания может списывать (терминал и пароль заданы, рубли): company.tinkoff_operable считает сервер, и
                 только когда у клиента есть привязанная карта: без неё сервер отвечает 406 -->
            <v-flex md12 v-if="company && company.tinkoff_operable && clientHasSavedCard" class="text-center">
              <v-switch v-model="charge_saved_card" :label="$t('tinkoff.tinkoff_enabled_for_this_payment')"
                :disabled="outcomeUnknown" data-testid="payment-charge-saved-card" v-tooltip="{
                content: $t('tinkoff.tinkoff_enabled_for_this_payment_tooltip'),
                placement: 'bottom-center',
                classes: ['info'],
                targetClasses: ['it-has-a-tooltip'],
                delay: {
                  show: 1000,
                  hide: 500,
                },
              }" color="warning"></v-switch>
            </v-flex>
            <!-- FT-193 W1: исход прошлого списания с карты неизвестен — повторить можно только им же, другой платёж —
                 только через «Начать новый платёж» с подтверждением. Блок вне условия переключателя: если терминал или
                 карта клиента пропали, оператор не должен остаться без выхода. -->
            <v-flex md12 v-if="outcomeUnknown" class="text-center" data-testid="payment-outcome-unknown">
              <p class="caption warning--text mb-2" data-testid="payment-outcome-unknown-hint">
                {{ $t("tinkoff.errors.outcome_unknown_locked") }}
              </p>
              <v-btn small outlined color="warning" :disabled="isLoading" @click="startNewPayment()"
                data-testid="payment-start-new">{{ $t("tinkoff.start_new_payment") }}</v-btn>
            </v-flex>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-actions>
        <v-row justify="center" class="mb-5">
          <v-btn text class="mr-3" @click="closePaymentDialog()">{{ $t("custom.close") }}</v-btn>
          <v-btn @click="savePayment(booking)" :loading="isLoading" color="success" data-testid="payment-save">{{ $t("custom.save") }}</v-btn>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import moment from "moment";
import "moment/locale/ru";
import "moment/locale/en-gb";
import { copyToClipboard as copyTextToClipboard } from '@/utils/clipboard';
import { sumMoney, toMoneyNumber } from './moneySum';
import RentToOwnPaymentSummary from "@/components/dashboard/rent_to_own/RentToOwnPaymentSummary.vue";
import { translateCountCategory } from "@/utils/countCategory";
import rentalObjectMixin from "@/mixins/rentalObjectMixin";
import { isTinkoffCardBound } from "@/utils/tinkoffCard";
export default {
  mixins: [rentalObjectMixin],
  name: 'PaymentDialog',
  components: { RentToOwnPaymentSummary },
  props: {
    // Приёмка 29.09: договор выкупа бронирования (из RentToOwnPanel через
    // BookingCard); у обычного бронирования — null.
    rentToOwnContract: {
      type: Object,
      default: null
    },
    value: {
      type: Boolean,
      default: false
    },
    // FT-193 NEG-02: прошлая отправка списания с карты не подтверждена (BookingCard.paymentOutcomeUnknown). Платёж
    // обязан повториться тем же способом и с тем же ключом, поэтому переключатель остаётся включённым и заперт.
    outcomeUnknown: {
      type: Boolean,
      default: false
    },
    booking: {
      type: Object,
      required: true
    },
    departure_payments: {
      type: Object,
      required: true
    },
    is_loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      counts_headers: [
        { text: this.$t("custom.number"), align: "center", value: "id" },
        { text: this.$t("custom.created_at"), value: "created_at", align: "center" },
        { text: this.$t("custom.amount"), value: "sum", align: "center" },
        { text: this.$t("custom.type"), value: "type", align: "center" },
        { text: this.$t("custom.group"), value: "group", align: "center" }
      ],
      dataPanelPaymentsInDialog: 0,
      showAddPayment: false,
      partReturnDeposit: false,
      company_cashbox_id: null,
      worker_account_id: null,
      // FT-193 REQ-11: списание с привязанной карты клиента; всегда выключено при открытии диалога.
      charge_saved_card: false,
    };
  },
  mounted() {
    let default_cashbox = this.active_company_cashboxes && this.active_company_cashboxes[0] && this.active_company_cashboxes[0].currency_id ? this.active_company_cashboxes[0] : null;
    this.departure_payments.rent.company_cashbox = default_cashbox;
    this.departure_payments.rent.currency_id = default_cashbox.currency_id;
    this.departure_payments.rent.company_cashbox_id = default_cashbox.id;
    this.departure_payments.deposit.company_cashbox = default_cashbox;
    this.departure_payments.deposit.currency_id = default_cashbox.currency_id;
    this.departure_payments.deposit.company_cashbox_id = default_cashbox.id;
    this.departure_payments.delivery.company_cashbox = default_cashbox;
    this.departure_payments.delivery.currency_id = default_cashbox.currency_id;
    this.departure_payments.delivery.company_cashbox_id = default_cashbox.id;
    this.departure_payments.delivery_end.company_cashbox = default_cashbox;
    this.departure_payments.delivery_end.currency_id = default_cashbox.currency_id;
    this.departure_payments.delivery_end.company_cashbox_id = default_cashbox.id;
    this.departure_payments.add_drivers_cost.company_cashbox = default_cashbox;
    this.departure_payments.add_drivers_cost.currency_id = default_cashbox.currency_id;
    this.departure_payments.add_drivers_cost.company_cashbox_id = default_cashbox.id;
    this.departure_payments.clean.company_cashbox = default_cashbox;
    this.departure_payments.clean.currency_id = default_cashbox.currency_id;
    this.departure_payments.clean.company_cashbox_id = default_cashbox.id;
    this.departure_payments.hours_cost.company_cashbox = default_cashbox;
    this.departure_payments.hours_cost.currency_id = default_cashbox.currency_id;
    this.departure_payments.hours_cost.company_cashbox_id = default_cashbox.id;
    this.departure_payments.insurance.company_cashbox = default_cashbox;
    this.departure_payments.insurance.currency_id = default_cashbox.currency_id;
    this.departure_payments.insurance.company_cashbox_id = default_cashbox.id;
    this.departure_payments.fine.company_cashbox = default_cashbox;
    this.departure_payments.fine.currency_id = default_cashbox.currency_id;
    this.departure_payments.fine.company_cashbox_id = default_cashbox.id;
    this.departure_payments.equipment.company_cashbox = default_cashbox;
    this.departure_payments.equipment.currency_id = default_cashbox.currency_id;
    this.departure_payments.equipment.company_cashbox_id = default_cashbox.id;
    // FT-102 (ревью PR-B, Codex): категория «Оборудование» без дефолтной кассы
    // уходила в save_count с currency_id/company_cashbox_id = null
    this.departure_payments.addons.company_cashbox = default_cashbox;
    this.departure_payments.addons.currency_id = default_cashbox.currency_id;
    this.departure_payments.addons.company_cashbox_id = default_cashbox.id;
    this.departure_payments.gas.company_cashbox = default_cashbox;
    this.departure_payments.gas.currency_id = default_cashbox.currency_id;
    this.departure_payments.gas.company_cashbox_id = default_cashbox.id;
    this.departure_payments.mileage.company_cashbox = default_cashbox;
    this.departure_payments.mileage.currency_id = default_cashbox.currency_id;
    this.departure_payments.mileage.company_cashbox_id = default_cashbox.id;
    this.departure_payments.damage.company_cashbox = default_cashbox;
    this.departure_payments.damage.currency_id = default_cashbox.currency_id;
    this.departure_payments.damage.company_cashbox_id = default_cashbox.id;
    this.departure_payments.other.company_cashbox = default_cashbox;
    this.departure_payments.other.currency_id = default_cashbox.currency_id;
    this.departure_payments.other.company_cashbox_id = default_cashbox.id;
  },
  watch: {
    // FT-193 REQ-11: каждое открытие диалога начинается с выключенного списания — прошлый выбор не переносится,
    // иначе следующий платёж по другому бронированию ушёл бы на карту без явного решения оператора. Исключение —
    // неподтверждённая отправка с карты: её повтор идёт на карту же (иначе сервер запишет обычные строки второй раз).
    // FT-198: переключатель закрытия удержания тоже не переживает повторное открытие окна.
    value(opened) {
      if (!opened) return;
      this.charge_saved_card = this.outcomeUnknown;
      this.partReturnDeposit = false;
    },
    // FT-193 W1: исход стал неизвестен при открытом окне — повтор пойдёт на карту, и снимок показывается целиком (строки
    // дополнительных услуг раскрыты). Неизвестный исход снят «Начать новый платёж» — списание с карты оператор
    // выбирает заново, как при обычном открытии.
    outcomeUnknown(unknown) {
      this.charge_saved_card = !!unknown;
      if (unknown) this.showAddPayment = true;
    },
  },
  computed: {
    // Карта клиента брони привязана (признак tinkoff_card_bound, не маска).
    clientHasSavedCard() {
      return isTinkoffCardBound(this.booking && this.booking.client);
    },
    // FT-198 REQ-04: закрыть удержание залога может только сотрудник и только при стоящем удержании.
    canReturnDeposit() {
      return Boolean(this.isStaff && this.booking && this.booking.part_return_deposit);
    },
    // Сводка договора — только у действующего договора: закрытому договору
    // платёж принимается как обычный (REQ-26), черновику — не принимается.
    rentToOwnActive() {
      return Boolean(this.rentToOwnContract && this.rentToOwnContract.lifecycle === "active");
    },
    payment_dialog: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    isLoading() {
      return this.is_loading;
    },
    company() {
      return this.$store.getters.getCompany ? this.$store.getters.getCompany.company : null;
    },
    company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id && cashbox.is_active) {
            cashboxes.push(cashbox);
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    active_company_cashboxes() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.company_cashboxes) {
        let cashboxes = [];
        this.$store.getters.getCompany.company.company_cashboxes.forEach((cashbox) => {
          if (cashbox.id && cashbox.is_active) {
            cashboxes.push(cashbox);
          }
        });
        cashboxes.sort((a, b) => {
          // сортировка по is_default
          if (a.is_default && !b.is_default) {
            return -1;
          }
        });
        return cashboxes;
      } else {
        return [];
      }
    },
    worker_accounts() {
      if (this.$store.getters.getCurrentUser) {
        return this.$store.getters.getCurrentUser.currency_accounts;
      } else {
        return [];
      }
    },
    currency() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currency
      ) {
        return this.$store.getters.getCompany.company.currency;
      } else {
        return "";
      }
    },
    currencies() {
      if (
        this.$store.getters.getCompany &&
        this.$store.getters.getCompany.company &&
        this.$store.getters.getCompany.company.currencies
      ) {
        return this.$store.getters.getCompany.company.currencies;
      } else {
        return [];
      }
    },
    base_currency_id() {
      if (this.active_company_cashboxes && this.active_company_cashboxes.length > 0) {
        return this.active_company_cashboxes.find((cashbox) => cashbox.is_default).currency_id;
      } else {
        return null;
      }
    },
    exchange_rates() {
      if (this.$store.getters.getCompany && this.$store.getters.getCompany.company && this.$store.getters.getCompany.company.exchange_rates) {
        return this.$store.getters.getCompany.company.exchange_rates;
      } else {
        return [];
      }
    },
    isMobile() {
      return this.$vuetify.breakpoint.name == "xs" || this.$vuetify.breakpoint.name == "sm";
    },
  },
  methods: {
    // Сводка договора — в базовой валюте; поле платежа — в валюте выбранной
    // кассы (как подсказка messageInBaseCurrency рядом), поэтому переводим.
    fillRentFromContract(amount) {
      // FT-193 W1: поля заперты на снимке незавершённого списания с карты
      if (this.outcomeUnknown) return;
      const rent = this.departure_payments.rent;
      const foreign = this.company && this.company.multicurrency_cashboxes && rent.currency_id &&
        rent.currency_id != this.base_currency_id;
      rent.sum = foreign ? parseFloat(this.sumInNonBaseCurrency(amount, rent.currency_id)) : amount;
    },
    closePaymentDialog() {
      this.$emit('close');
    },
    // Переключатель считается только пока компания может списывать с карты: сервер ответил бы отказом.
    chargeSavedCardRequested() {
      return !!(this.charge_saved_card && this.company && this.company.tinkoff_operable && this.clientHasSavedCard);
    },
    savePayment(booking) {
      // FT-198 REQ-04: четвёртый аргумент — закрыть удержание залога после успешного платежа. При повторе
      // неподтверждённого списания с карты карточка берёт его из снимка первой отправки, а не отсюда.
      const closeDepositHold = Boolean(this.partReturnDeposit && this.canReturnDeposit);
      this.$emit('savePayment', booking, this.departure_payments, this.chargeSavedCardRequested(), closeDepositHold);
    },
    // FT-193 W1: подтверждение и сброс — в BookingCard.startNewPayment
    startNewPayment() {
      this.$emit("start-new-payment");
    },
    currentDeposit() {
      if (!this.booking.hourly && !this.booking.weekly && !this.booking.monthly) {
        let check_number = Number(this.booking.deposit) === this.booking.deposit;
        return check_number ? parseFloat(this.booking.deposit.toFixed(2)) : 0;
      } else if (this.booking.hourly) {
        let check_number = Number(this.booking.hourly_deposit) === this.booking.hourly_deposit;
        return check_number ? parseFloat(this.booking.hourly_deposit.toFixed(2)) : 0;
      } else if (this.booking.weekly) {
        let check_number = Number(this.booking.weekly_deposit) === this.booking.weekly_deposit;
        return check_number ? parseFloat(this.booking.weekly_deposit.toFixed(2)) : 0;
      } else if (this.booking.monthly) {
        let check_number = Number(this.booking.monthly_deposit) === this.booking.monthly_deposit;
        return check_number ? parseFloat(this.booking.monthly_deposit.toFixed(2)) : 0;
      } else {
        let check_number = Number(this.booking.deposit) === this.booking.deposit;
        return check_number ? parseFloat(this.booking.deposit.toFixed(2)) : 0;
      }
    },
    sumInBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) / parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    sumInNonBaseCurrency(sum, currency_id) {
      if (this.exchange_rates.length > 0 && currency_id) {
        let exchange_rate = this.exchange_rates.find((exchange_rate) => exchange_rate.target_currency_id == currency_id);
        if (exchange_rate) {
          return (parseFloat(sum) * parseFloat(exchange_rate.rate)).toFixed(2);
        } else {
          return sum / 1;
        }
      } else {
        return sum / 1;
      }
    },
    messageInBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(this.base_currency_id)}`;
      } else {
        return '';
      }
    },
    messageInNonBaseCurrency(sum, currency_id) {
      if (sum && currency_id && this.base_currency_id && this.base_currency_id != currency_id) {
        return `${this.sumInNonBaseCurrency(sum, currency_id)} ${this.findCurrencySymbol(currency_id)}`;
      } else {
        return '';
      }
    },
    findCurrencySymbol(id) {
      if (id != null) {
        if (this.currencies.find((currency) => currency.id == id)) {
          return this.currencies.find((currency) => currency.id == id).symbol;
        }
      }
    },
    // Класс брони 664209 (2026-07-21, как bookingTotalPreview в BookingCard):
    // booking — живой объект карточки, очищенный инпут даёт "" — сырой `+`
    // превращал сумму в конкатенацию строк (3.2e+22 на экране, а сравнение
    // с totalGettingSum() для галочки «принято» ломалось всегда).
    // fine добавлен холистик-ревью 2026-07-21 (pre-existing дыра формулы:
    // поле «Штраф» в диалоге есть и totalGettingSum его считает, а «Итого»
    // не считало — галочка «принято» не сходилась при оплате штрафа).
    totalSum() {
      return sumMoney([
        this.booking.rental_cost,
        this.currentDeposit(),
        this.booking.equipment,
        this.booking.addons_cost, // FT-102: каталожное оборудование
        this.booking.add_drivers_cost,
        this.booking.gas,
        this.booking.mileage_cost,
        this.booking.clean_payment,
        this.booking.delivery,
        this.booking.delivery_end,
        this.booking.damage,
        this.booking.hours_cost,
        this.booking.insurance,
        this.booking.fine,
        this.booking.other,
      ]) - toMoneyNumber(this.booking.aggr_commission);
    },
    totalGettingSum() {
      let sum = 0;
      Object.values(this.departure_payments).forEach((payment) => {
        if (payment.sum && typeof payment.sum === 'number') {
          // сумма в базовой валюте
          sum += parseFloat(this.sumInBaseCurrency(payment.sum, payment.currency_id));
        }
      });
      return sum;
    },
    async copyToBuffer(text) {
      try {
        await copyTextToClipboard(text);
        this.$notify(this.$t("custom.copied_to_buffer"), "", "success");
      } catch (error) {
        this.$notify(this.$t("custom.copy_error"), "", "error");
      }
    },
    formatCreatedAt(date) {
      return moment.parseZone(date).format("DD MMM H:mm");
    },
    translateCountGroup(category) {
      return translateCountCategory((key) => this.$t(key), category);
    }
  }
}
</script>

